import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import {
  ConfirmModalStatus,
  ModalLoading,
  PrimaryButton,
} from '../../components'
import { DeliveryEnum, PaymentModeId } from '../../interfaces'
import {
  authorizedServiceOptions,
  valueDeclareServiceIdsFor,
} from '../../utils/authorizedServices'
import { useAppSelector } from '../../store/hooks'
import { useContractDestinationBUs } from '../../hooks/useContractDestinationBUs'
import {
  isJuniorStore,
  resolveDestinationBusinessUnits,
} from '../../utils/destinationBusinessUnits'
import {
  createImport,
  updateImport,
} from '../../services/ShipmentBulkUploadServices'
import { useAllowedServices } from '../../hooks/useAllowedServices'
import { usePieceDimensionMax } from '../../hooks/usePieceDimensionMax'
import { useNavigate } from 'react-router-dom'
import { alertService, getChargedWeight } from '../../services'
import { buildShipmentList } from '../../utils/shipmentPayload'
import { unauthorizedOriginStoreCodes } from '../../utils/originBusinessUnits'
import { originOfficeDisplay } from '../../utils/bulkTemplateDisplay'
import { bulkUploadOwnerFilter } from '../../utils/bulkUploadOwnership'
import {
  bulkChargedWeight,
  validateGroup as validateGroupShared,
  validatePiece as validatePieceShared,
} from '../../utils/shipmentValidation'
import {
  messageForCode,
  mapServerFieldErrors,
} from '../../utils/shipmentBulkUploadStatus'
import { ShipmentBulkUploadAccordionList } from '../../components/ShipmentBulkUpload/ShipmentBulkUploadAccordionList'
import { getStoreByCoverageDjango } from '../../services'
import { getAreaCodesDjango } from '../../services/GeneralServices'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import {
  changeCountryCode,
  reverseGeocodeCoords,
  COORD_OUT_OF_ZONE_MSG,
} from '../../utils/shipmentImportUtils'

const ShipmentBulkUploadReview: FC<{
  groups: ShipmentGroup[]
  setGroups: (groups: ShipmentGroup[]) => void
  setNext: (val: boolean) => void
  isCOD: boolean
  businessUnits: any[]
  identificationTypes: any[]
  citiesType: any[]
  citiesByBu: any[]
  pieceCategories: any[]
  favoriteDestinations?: DestinationInterface[]
  file?: File
  externalManifest?: string
  /** 'create' (default) llama createImport; 'updateImport' corrige una importación existente. */
  submitMode?: 'create' | 'updateImport'
  /**
   * Errores por fila/campo con los que ARRANCA la pantalla, traducidos del
   * `error_message` que el backend guardó al intentar crear cada guía. Los usa
   * Corregir errores para que el operador vea de entrada cuál guía falló y por
   * qué, sin tener que volver al detalle.
   */
  initialServerErrors?: Record<number, Record<string, string>>
  /**
   * Mínimo de valor declarado que el backend exigió, por `importDetailId`.
   *
   * Va aparte de `initialServerErrors` porque no es un mensaje congelado sino una
   * regla que se re-evalúa contra la suma de las piezas mientras el operador
   * edita: el aviso dice cuánto falta y desaparece solo al llegar al mínimo.
   *
   * Y va por id de detalle, no por índice como `initialServerErrors`, porque este
   * dato NO se limpia al editar: sobrevive a un merge de guías, y con índices una
   * fila heredaría el mínimo de otra y mostraría un monto que no le corresponde.
   */
  initialMinDeclaredValue?: Record<string, number>
  /** Requerido cuando submitMode='updateImport': el header a corregir. */
  headerId?: string
}> = ({
  groups: initialGroups,
  setGroups: setParentGroups,
  setNext,
  isCOD,
  businessUnits,
  identificationTypes,
  citiesType,
  citiesByBu,
  pieceCategories,
  favoriteDestinations = [],
  file,
  externalManifest,
  submitMode = 'create',
  headerId,
  initialServerErrors,
  initialMinDeclaredValue,
}) => {
  const user = useAppSelector((s) => s.user)
  const rangeDeclareList = useAppSelector((s) => s.inmutable.rangeDeclareList)
  // Países del teléfono del destinatario. Se leen del slice (los dos flujos que
  // montan el Review —creación y corrección— hidratan `bulkCatalogs`) en vez de
  // prop-drillearlos desde dos módulos distintos.
  const countryOptions = useAppSelector((s) => s.bulkCatalogs.countryPhoneCodes)
  const countryCatalogFailed = useAppSelector(
    (s) => s.bulkCatalogs.countryPhoneCodesError
  )
  // Solo para GetChargedWeight; el dueño de la carga sale de bulkUploadOwnerFilter.
  const accountSiteId = user.client?.accountSiteID || undefined
  const accountId = user.client?.id || undefined
  const paymentModeRaw =
    user.paymentMethod?.[0]?.paymentModeID ?? PaymentModeId.CREDIT
  const { allowedServices } = useAllowedServices(
    paymentModeRaw as PaymentModeId
  )
  const navigate = useNavigate()

  // Factor volumétrico del sistema (cm³/kg), sondeado UNA vez con una pieza
  // unitaria — de la respuesta solo interesa model.volumetricFactor. Con él la
  // validación calcula el peso a facturar de cada guía en local (llamar a
  // GetChargedWeight por guía sería una petición por fila, y una carga trae
  // miles). Si la sonda falla queda null y solo se valida el peso físico; el
  // backend sigue rechazando el resto (shipping.ShipmentHeader.35, ya traducido).
  const [volumetricFactor, setVolumetricFactor] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    getChargedWeight(accountId, accountSiteId, undefined, undefined, [
      { weight: 1, height: 1, width: 1, length: 1 },
    ]).then((res) => {
      if (cancelled || res.didError || !res.model) return
      setVolumetricFactor(res.model.volumetricFactor || null)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Servicios elegibles por guía. El helper es compartido con la generación de la
  // plantilla Excel (columna "Servicio"): las dos listas DEBEN ser la misma o el operador
  // elegiría en Excel un servicio que acá se le reemplazaría.
  const serviceOptions = useMemo(
    () => authorizedServiceOptions(allowedServices, user.client, paymentModeRaw),
    [allowedServices, user.client, paymentModeRaw]
  )

  // serviceIDs que exigen valor declarado (isValueDeclare) para la modalidad/sitio actual.
  // Mismo criterio que ShipmentFormV2: la guía "viaja asegurada" si su servicio está aquí.
  // Se filtra igual que serviceOptions (por modalidad y accountSite) para no arrastrar
  // autorizaciones de otras modalidades.
  const valueDeclareServiceIds = useMemo(
    () => valueDeclareServiceIdsFor(user.client, paymentModeRaw),
    [user.client, paymentModeRaw]
  )

  // Tiendas destino que el contrato permite (AgreementBU). MISMA fuente y MISMA
  // regla que la plantilla Excel: lo que el operador pudo elegir en el archivo tiene
  // que ser exactamente lo que puede elegir acá. Se resuelve UNA vez y se baja a las
  // filas por props; si cada fila llamara al hook, con la caché fría una página de
  // 25 filas dispararía 25 peticiones. `restricted` distingue "el contrato no
  // limita" de "limita y ninguna tienda quedó vigente".
  const { rows: contractDestinationRows } = useContractDestinationBUs(
    user.client?.agreementID
  )
  const { options: destinationOptions, restricted: destinationsRestricted } =
    useMemo(
      () =>
        resolveDestinationBusinessUnits(
          businessUnits,
          contractDestinationRows,
          new Date()
        ),
      [businessUnits, contractDestinationRows]
    )
  // Códigos permitidos para la validación por fila. Vacío cuando el contrato no
  // restringe → la validación no exige nada, que es el comportamiento de siempre.
  const allowedDestinationBuCodes = useMemo(
    () =>
      destinationsRestricted ? destinationOptions.map((bu) => bu.buCode) : [],
    [destinationsRestricted, destinationOptions]
  )

  /**
   * Tiendas Junior que NO quedaron ofrecidas. El desplegable ya no las muestra, pero el
   * operador puede escribir el código a mano en el Excel o reutilizar un archivo viejo, y
   * `allowedDestinationBuCodes` solo valida algo cuando el contrato restringe.
   *
   * Se restan las que SÍ están en `destinationOptions` a propósito: un contrato que lista
   * explícitamente una Junior la habilita (la rama restringida de
   * `resolveDestinationBusinessUnits` ignora el tipo), y bloquearla acá contradiría al
   * desplegable que la acaba de ofrecer.
   */
  const blockedStoreCodes = useMemo(() => {
    const norm = (code?: string) => (code ?? '').trim().toUpperCase()
    const offered = new Set(destinationOptions.map((bu) => norm(bu.buCode)))
    return businessUnits
      .filter(isJuniorStore)
      .map((bu) => bu.buCode)
      .filter((code) => !offered.has(norm(code)))
  }, [businessUnits, destinationOptions])

  const [localGroups, setLocalGroups] = useState<ShipmentGroup[]>(initialGroups)

  useEffect(() => {
    if (serviceOptions.length === 0) return
    const validIds = new Set(serviceOptions.map((s) => s.serviceID))
    setLocalGroups((prev) => {
      let changed = false
      const next = prev.map((g) => {
        if (g.serviceId != null && validIds.has(g.serviceId)) return g
        changed = true
        return { ...g, serviceId: serviceOptions[0].serviceID }
      })
      return changed ? next : prev
    })
  }, [serviceOptions])

  // Keep parent in sync so auto-save in ShipmentBulkUpload.tsx captures edits made in Review
  useEffect(() => {
    setParentGroups(localGroups)
  }, [localGroups, setParentGroups])

  const [mergeModal, setMergeModal] = useState<{ sourceIndex: number } | null>(
    null
  )

  // V3: phonePrefixList for operadora dropdown in AccordionRow
  const [phonePrefixList, setPhonePrefixList] = useState<
    { value: string; text: string }[]
  >([])
  useEffect(() => {
    let cancelled = false
    // API Django: /api/area-code?country_code=58 — prefijos de operadora (reemplaza el .NET
    // ListofType/listCodePhone). Ojo: aquí el país va como código telefónico (58), no como
    // el CountryId .NET (236). NO usar /api/phone-type: eso son TIPOS (Móvil/Fijo), no prefijos.
    getAreaCodesDjango('58')
      .then((res: any) => {
        if (cancelled) return
        const list = res?.didError ? [] : res?.model || []
        setPhonePrefixList([...list, { value: 'FIJO', text: 'FIJO' }])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  // V3: Map<RIF, DestinationInterface> for O(1) RIF auto-suggest lookup
  const favsByRif = useMemo(() => {
    const m = new Map<string, DestinationInterface>()
    favoriteDestinations.forEach((f) => {
      const rif = (f.account?.identificationNumber || '').trim()
      if (rif) m.set(rif, f)
    })
    return m
  }, [favoriteDestinations])

  // V3: Maps validation progress only — user preference 2026-05-15 removed the
  // global coord stats card and coordWarnings banner. Per-row warnings are
  // surfaced via importWarning inside each AccordionRow.
  const [coordValidating, setCoordValidating] = useState(false)
  const [coordProgress, setCoordProgress] = useState<{
    current: number
    total: number
  }>({ current: 0, total: 0 })

  // V3: Maps validation on mount — throttle 5 parallel, batched setState for progressive UX
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // A2: aborta las (hasta 200) llamadas de cobertura si el usuario sale de Review a
    // mitad de lote (cleanup). getStoreByCoverageDjango recibe el signal y corta.
    const coverageController = new AbortController()
    const tasks = initialGroups
      .map((g, i) => ({ g, i }))
      .filter(
        ({ g }) =>
          g.deliveryType === DeliveryEnum.DELIVERY &&
          g.latitude != null &&
          g.longitude != null &&
          !g.favoriteDestinationId
      )

    const total = tasks.length
    if (total === 0) return

    setCoordValidating(true)
    setCoordProgress({ current: 0, total })
    ;(async () => {
      const BATCH_SIZE = 5
      for (let i = 0; i < tasks.length; i += BATCH_SIZE) {
        if (!mountedRef.current) return
        const batch = tasks.slice(i, i + BATCH_SIZE)
        const batchResults = await Promise.all(
          batch.map(async ({ g, i: idx }) => {
            try {
              // Territory lookup gives us the assigned TEALCA store (buCode +
              // locationCode). Its location.address is the warehouse address,
              // NOT the customer's, so we reverse-geocode the coords separately
              // to get the actual destinationAddress for the operator.
              const [res, geo] = await Promise.all([
                getStoreByCoverageDjango(
                  g.longitude!,
                  g.latitude!,
                  coverageController.signal
                ),
                reverseGeocodeCoords(g.latitude!, g.longitude!),
              ])
              if (!res.didError && res.model) {
                return {
                  index: idx,
                  patch: {
                    destinationAddress: geo?.address || g.destinationAddress,
                    destinationCity:
                      res.model.location?.locationCode || g.destinationCity,
                    storeCode: res.model.buCode || g.storeCode,
                    postalCode:
                      geo?.postalCode ||
                      res.model.location?.postalCode ||
                      g.postalCode,
                    // Clear any prior coordWarning from a previous validation pass
                    coordWarning: undefined,
                  } as Partial<ShipmentGroup>,
                }
              }
              // Coordenadas fuera de zona: NO se limpian ni se resuelve por la ciudad del
              // archivo. Se conservan tal cual y se le pide al operador corregirlas. El
              // coordWarning bloquea el envío (ver hasAnyError, 4A).
              return {
                index: idx,
                patch: {
                  coordWarning: COORD_OUT_OF_ZONE_MSG,
                } as Partial<ShipmentGroup>,
              }
            } catch (_) {
              return {
                index: idx,
                patch: {
                  coordWarning: COORD_OUT_OF_ZONE_MSG,
                } as Partial<ShipmentGroup>,
              }
            }
          })
        )
        if (!mountedRef.current) return

        // Apply this batch's results progressively (pure transform — safe in StrictMode)
        setLocalGroups((prev) => {
          const next = [...prev]
          for (const r of batchResults) {
            next[r.index] = { ...next[r.index], ...r.patch }
          }
          return next
        })
        setCoordProgress({ current: Math.min(i + BATCH_SIZE, total), total })
      }

      if (!mountedRef.current) return
      setCoordValidating(false)
    })()
    return () => coverageController.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRequestMerge = (sourceIndex: number) => {
    setMergeModal({ sourceIndex })
  }

  const handleConfirmMerge = () => {
    if (!mergeModal) return
    const { sourceIndex } = mergeModal
    setLocalGroups((prev) => {
      const source = prev[sourceIndex]
      const targetIdx = prev.findIndex(
        (g, i) =>
          i !== sourceIndex && g.shipmentHeaderReference === source.shipmentHeaderReference
      )
      if (targetIdx < 0) return prev
      const pKeyOf = (p: ShipmentPieceRow) =>
        [
          p.productCode,
          String(p.weight),
          String(p.height),
          String(p.width),
          String(p.length),
        ].join('|')
      const pMap = new Map<string, ShipmentPieceRow>()
      for (const p of prev[targetIdx].pieces) pMap.set(pKeyOf(p), { ...p })
      for (const p of source.pieces) {
        const k = pKeyOf(p)
        if (pMap.has(k)) {
          const ep = pMap.get(k)!
          ep.quantity += p.quantity
          ep.mergedRowCount = (ep.mergedRowCount || 1) + (p.mergedRowCount || 1)
        } else {
          pMap.set(k, { ...p })
        }
      }
      const merged = Array.from(pMap.values())
      const updated = prev
        .map((g, i) => (i === targetIdx ? { ...g, pieces: merged } : g))
        .filter((_, i) => i !== sourceIndex)
      return updated
    })
    setMergeModal(null)
  }

  const [loading, setLoading] = useState(false)
  const [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING
  )
  const [loadingTitle, setLoadingTitle] = useState('Procesando…')

  // Topes por dimensión (cm) del Facturador; sin parámetro configurado no hay tope.
  const dimensionMax = usePieceDimensionMax()

  // Delegado al util compartido (eng-review E1): única fuente de la validación de grupo.
  const validateGroup = useCallback(
    (g: ShipmentGroup): ShipmentGroupErrors =>
      validateGroupShared(g, {
        citiesByBu,
        businessUnits,
        isCOD,
        rangeDeclareList,
        valueDeclareServiceIds,
        originBusinessUnits: user.originBusinessUnits ?? [],
        allowedDestinationBuCodes,
        blockedStoreCodes,
        dimensionMax,
        // Peso a facturar contra el maxWeight del servicio de la guía.
        volumetricFactor,
        services: serviceOptions,
        // Resuelve el tipo de documento de cada parte para validar el nombre con
        // la regla que corresponde (jurídico admite `+` y una sola palabra).
        identificationTypes,
      }),
    [
      citiesByBu,
      businessUnits,
      isCOD,
      rangeDeclareList,
      valueDeclareServiceIds,
      user.originBusinessUnits,
      allowedDestinationBuCodes,
      blockedStoreCodes,
      dimensionMax,
      volumetricFactor,
      serviceOptions,
      identificationTypes,
    ]
  )

  // Validación de pieza compartida, con el mínimo por peso+servicio. El servicio es por
  // grupo, así que el AccordionRow lo inyecta al llamar (serviceId del grupo, más las
  // piezas del grupo y el índice de la pieza para evaluar el peso a facturar).
  const validatePiece = useCallback(
    (
      p: ShipmentPieceRow,
      serviceId?: number,
      groupPieces?: ShipmentPieceRow[],
      replaceIndex?: number
    ): Partial<Record<keyof ShipmentPieceRow, string>> => {
      // Sobrepeso de la GUÍA evaluado con las piezas candidatas: las del grupo con
      // esta pieza sustituyendo a la suya. Así el borrador del modal se valida
      // contra el total real, y bajar dimensiones/peso desbloquea el Guardar del
      // modal en el momento (no después de guardar).
      let chargedOverweight: { byDimensions: boolean } | null = null
      const serviceMaxWeight = serviceOptions.find(
        (s) => s.serviceID === serviceId
      )?.maxWeight
      if (serviceMaxWeight && groupPieces?.length) {
        const candidate =
          replaceIndex != null &&
          replaceIndex >= 0 &&
          replaceIndex < groupPieces.length
            ? groupPieces.map((x, i) => (i === replaceIndex ? p : x))
            : groupPieces
        const { physical, dimensional, charged } = bulkChargedWeight(
          candidate,
          volumetricFactor
        )
        if (charged > serviceMaxWeight)
          chargedOverweight = { byDimensions: dimensional > physical }
      }
      return validatePieceShared(p, {
        rangeDeclareList,
        serviceId,
        requireDeclaredValue:
          isCOD || (serviceId != null && valueDeclareServiceIds.has(serviceId)),
        // Tope por dimensión del Facturador; reemplaza el 300 que estaba cableado.
        dimensionMax,
        chargedOverweight,
      })
    },
    [
      rangeDeclareList,
      isCOD,
      valueDeclareServiceIds,
      dimensionMax,
      serviceOptions,
      volumetricFactor,
    ]
  )

  // Errores que devolvió el backend por campo/fila (general.General.5). Se fusionan
  // con la validación cliente y se limpian cuando el operador edita esa fila. Sirven
  // de red de seguridad para reglas del backend que el cliente no replica.
  const [serverErrors, setServerErrors] = useState<
    Record<number, Record<string, string>>
  >(() => {
    const seed: Record<number, Record<string, string>> = {
      ...(initialServerErrors ?? {}),
    }
    /**
     * El mínimo de valor declarado pasa a ser una regla VIVA (se recalcula abajo
     * contra la suma de las piezas), así que el mensaje estático que vino del
     * backend sobra. Dejarlo mostraría el total viejo aunque el operador ya haya
     * subido los montos, y `clearServerErrorsForRow` lo borraría al primer tecleo
     * — justo cuando el aviso más sirve.
     */
    initialGroups.forEach((g, i) => {
      const detailId = g.importDetailId || ''
      if (!detailId || (initialMinDeclaredValue ?? {})[detailId] == null) return
      if (!seed[i] || seed[i].pieces === undefined) return
      const rest = { ...seed[i] }
      delete rest.pieces
      if (Object.keys(rest).length === 0) delete seed[i]
      else seed[i] = rest
    })
    return seed
  })

  /**
   * Mínimo exigido por el backend, por id de detalle. NO se limpia al editar (a
   * diferencia de `serverErrors`): la regla sigue valiendo hasta que la suma de
   * las piezas la satisface, que es cuando el aviso se apaga solo.
   */
  const [minDeclaredByDetail] = useState<Record<string, number>>(
    initialMinDeclaredValue ?? {}
  )

  const clientErrors: ShipmentGroupErrors[] = useMemo(() => {
    const trackingCount = new Map<string, number>()
    localGroups.forEach((g) => {
      const t = g.shipmentHeaderReference
      trackingCount.set(t, (trackingCount.get(t) || 0) + 1)
    })
    return localGroups.map((g) => {
      const e = validateGroup(g)
      if ((trackingCount.get(g.shipmentHeaderReference) || 0) > 1)
        e.shipmentHeaderReference =
          'Número de guía duplicado en esta carga masiva — cámbialo o combina los grupos'

      /**
       * Mínimo de valor declarado que el backend rechazó, re-evaluado en vivo.
       *
       * El total es la MISMA suma que viaja al backend (valor × cantidad de cada
       * pieza), así que el aviso dice exactamente cuánto falta y se apaga solo
       * cuando la suma llega. Sin esto, el operador veía un mensaje fijo con el
       * total viejo y no sabía si ya había alcanzado el mínimo.
       *
       * Es distinto del mínimo POR PIEZA que valida `validatePiece` con la tabla
       * de rangos: ese es la regla local por peso+servicio, este es el veredicto
       * que ya dio el backend sobre el total de la guía.
       */
      const min = g.importDetailId
        ? minDeclaredByDetail[g.importDetailId]
        : undefined
      if (min != null && !e.pieces) {
        const total = g.pieces.reduce(
          (acc, pc) => acc + (pc.declaredValue || 0) * (pc.quantity || 1),
          0
        )
        // Tolerancia de un centavo: el total sale de una suma de floats y un
        // 44.999999999999996 contra un mínimo de 45 no es un error real.
        if (total < min - 0.005) {
          const falta = min - total
          e.pieces =
            `El valor declarado total debe ser al menos $${min.toFixed(2)} — ` +
            `las piezas suman $${total.toFixed(2)}, faltan $${falta.toFixed(2)}`
        }
      }
      return e
    })
  }, [localGroups, validateGroup, minDeclaredByDetail])

  // El error cliente (live) tiene prioridad sobre el del servidor (que puede quedar
  // viejo): por eso va último en el spread, sobreescribiendo la misma key.
  //
  // EXCEPCIÓN: `validateGroup` usa la cadena VACÍA como marcador — `e.pieces = ''`
  // marca la fila como inválida sin texto, porque el detalle se ve por pieza. Si la
  // dejáramos ganar, borraría un mensaje del servidor con texto real (p. ej. "El valor
  // declarado no puede ser menor a $53.00") y la guía quedaría marcada en rojo sin decir
  // por qué. Con texto de los dos lados sigue ganando el cliente: es el que está vivo.
  const errors: ShipmentGroupErrors[] = useMemo(
    () =>
      clientErrors.map((e, i) => {
        const fromServer = serverErrors[i] || {}
        const merged: ShipmentGroupErrors = { ...fromServer, ...e }
        Object.entries(fromServer).forEach(([key, serverMsg]) => {
          const k = key as keyof ShipmentGroupErrors
          if (serverMsg && merged[k] === '') merged[k] = serverMsg
        })
        return merged
      }),
    [clientErrors, serverErrors]
  )

  const hasAnyError = useMemo(
    () =>
      localGroups.some((g, i) => {
        if (g.isDuplicateInDB) return true
        // 4A: coordenadas fuera de zona (o sin validar) bloquean el envío. Sin esto, un
        // coordWarning es solo un aviso suave y el operador podría enviar coords malas.
        if (g.coordWarning) return true
        return Object.keys(errors[i] || {}).length > 0
      }),
    [errors, localGroups]
  )

  const summary = useMemo(() => {
    const totalGroups = localGroups.length
    const totalPieces = localGroups.reduce(
      (acc, g) => acc + g.pieces.reduce((s, p) => s + (p.quantity || 1), 0),
      0
    )
    const totalDeclaredValue = localGroups.reduce(
      (acc, g) =>
        acc +
        g.pieces.reduce(
          (s, p) => s + (p.declaredValue || 0) * (p.quantity || 1),
          0
        ),
      0
    )
    // FINDING-013: kg totales del envío (sum weight × quantity across all pieces).
    const totalWeight = localGroups.reduce(
      (acc, g) =>
        acc +
        g.pieces.reduce((s, p) => s + (p.weight || 0) * (p.quantity || 1), 0),
      0
    )
    const errorCount = localGroups.filter(
      (g, i) => Object.keys(errors[i] || {}).length > 0 || !!g.coordWarning
    ).length
    return {
      totalGroups,
      totalPieces,
      totalDeclaredValue,
      totalWeight,
      errorCount,
    }
  }, [localGroups, errors])

  const paymentMode = paymentModeRaw

  const handleSubmit = async () => {
    if (!localGroups.length) return

    setServerErrors({}) // descartá errores de servidor de un intento anterior
    setLoading(true)
    setLoadingStatus(ConfirmModalStatus.PROCESSING)
    setLoadingTitle('Procesando…')

    try {
      const { shipment_list } = buildShipmentList(localGroups, {
        user,
        businessUnits,
        citiesByBu,
        identificationTypes,
        allowedServices,
        paymentMode,
        externalManifest,
        originBusinessUnits: user.originBusinessUnits ?? [],
      })

      const isUpdate = submitMode === 'updateImport' && !!headerId

      // Corrección (Approach A): PUT /update con `corrections[]`. Cada corrección
      // referencia su shipment_bulk_upload_detail_id (la línea en error) y manda el payload
      // corregido completo → el backend actualiza esa línea in-place (108 → 106) y la
      // reprocesa. shipment_list[i] alinea con localGroups[i] (buildShipmentList mapea
      // en orden), así que zippeamos por índice.
      const res = isUpdate
        ? await updateImport(headerId!, {
            corrections: localGroups.map((g, i) => ({
              shipment_bulk_upload_detail_id: g.importDetailId ?? '',
              shipment: shipment_list[i],
            })),
          })
        : await createImport({
            // Flujo asíncrono: crea la importación (multipart con archivo si existe)
            // y redirige a la vista de progreso. Reemplaza el POST /shipment síncrono.
            dto: {
              bulk_upload_number: null,
              source_reason: null,
              external_manifest: externalManifest?.trim() || null,
              // Dueño de la carga (al menos uno requerido por el backend, si no → .12).
              // Misma regla que el listado y que la guarda del detalle; las claves
              // en `undefined` las descarta JSON.stringify.
              ...bulkUploadOwnerFilter(user.client),
              shipment_list,
            },
            file,
          })

      if (!res.didError) {
        try {
          localStorage.removeItem('shipment-import-v2-draft')
        } catch (_) {}
        // source_file_uploaded:false con archivo enviado → la importación corre igual.
        if (
          !isUpdate &&
          file &&
          (res.model as any)?.source_file_uploaded === false
        ) {
          alertService.warn(
            'Carga masiva creada sin archivo',
            'El archivo no se pudo archivar; la carga masiva corre igual.'
          )
        }
        setLoading(false)
        setNext(false)
        const targetId = isUpdate
          ? headerId!
          : (res.model as any)?.shipment_bulk_upload_header_id
        if (isUpdate) {
          alertService.success(
            'Guías corregidas',
            'Volvé a ejecutar si es necesario.'
          )
        }
        navigate(`/shipment/bulk-upload/${targetId}`)
      } else {
        // Mapeá error_data → errores por fila/campo para marcarlos en el Review
        // (las filas con error se auto-expanden). Lo que no se pudo mapear a un
        // campo editable se anexa al mensaje del modal para no perder contexto.
        const mapped = mapServerFieldErrors(res.errorData)
        if (Object.keys(mapped.byIndex).length > 0) {
          setServerErrors(mapped.byIndex)
        }
        setLoadingStatus(ConfirmModalStatus.ERROR)
        const baseMsg = messageForCode(res.code, res.errorMessage)
        setLoadingTitle(
          mapped.unmapped.length > 0
            ? `${baseMsg} ${mapped.unmapped.join(' ')}`
            : baseMsg
        )
      }
    } catch (e: any) {
      setLoadingStatus(ConfirmModalStatus.ERROR)
      setLoadingTitle(e?.message || 'Error preparando el envío')
    }
  }

  /**
   * Descarta el error de servidor de una fila porque el operador acaba de editarla.
   *
   * Va en TODAS las mutaciones de la fila, incluidas las de piezas: un error como "El
   * valor declarado no puede ser menor a $53.00" se corrige editando una PIEZA, y si
   * solo lo limpiara `handleChangeGroup` la guía quedaría marcada y bloqueada para
   * siempre — el operador corrige el monto y el botón de enviar sigue deshabilitado.
   */
  const clearServerErrorsForRow = (index: number) => {
    setServerErrors((prev) => {
      if (!prev[index]) return prev
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  /**
   * Una edición de PIEZA además descarta el motivo crudo del rechazo: los errores
   * que llegan sin campo identificable (peso total facturado, dimensiones) se
   * corrigen justamente ahí, y dejar el texto viejo hace ver la guía como
   * rechazada después de arreglarla.
   *
   * Solo en piezas, no en `handleChangeGroup`: los chequeos de duplicado y de
   * coordenadas de la fila disparan `onChangeGroup` solos al montar, así que
   * colgarlo de ahí borraría el motivo antes de que nadie lo lea. A nivel de guía
   * el banner se cierra a mano.
   */
  const clearServerErrorNote = (index: number) => {
    setLocalGroups((prev) =>
      prev[index]?.serverErrorNote
        ? prev.map((g, i) =>
            i === index ? { ...g, serverErrorNote: undefined } : g
          )
        : prev
    )
  }

  const handleChangeGroup = (index: number, patch: Partial<ShipmentGroup>) => {
    setLocalGroups((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], ...patch }
      return next
    })
    clearServerErrorsForRow(index)
  }

  /**
   * Aplica un mismo patch a varias guías de una vez (barra de acciones masivas).
   *
   * Un archivo de aliado trae cientos de guías y `validateGroup` exige oficina
   * origen y servicio POR guía cuando el contrato tiene más de una opción: sin
   * esto, el operador tendría que abrir y cerrar el acordeón una vez por guía.
   *
   * Recorre los grupos una sola vez (no un `map` por índice) para que 403 filas
   * sigan siendo una sola pasada, y limpia el error de servidor de cada fila
   * tocada igual que `handleChangeGroup`.
   */
  const handleApplyBulk = (indices: number[], patch: Partial<ShipmentGroup>) => {
    if (indices.length === 0 || Object.keys(patch).length === 0) return
    const target = new Set(indices)
    setLocalGroups((prev) =>
      prev.map((g, i) => {
        if (!target.has(i)) return g
        // El país del teléfono NO se puede aplicar con un spread a secas: los
        // dígitos de cada fila hay que reescribirlos a la convención del país
        // nuevo. Sin esto, un lote venezolano al que se le pone Colombia queda
        // con "0573001234567" intacto y el backend rechaza las 150 guías.
        const countryPatch = patch.consigneePhoneCountryCode
          ? changeCountryCode(g, patch.consigneePhoneCountryCode)
          : null
        return { ...g, ...patch, ...countryPatch }
      })
    )
    setServerErrors((prev) => {
      const touched = indices.filter((i) => prev[i])
      if (touched.length === 0) return prev
      const next = { ...prev }
      touched.forEach((i) => delete next[i])
      return next
    })
  }

  const handleChangePiece = (
    groupIndex: number,
    pieceIndex: number,
    patch: Partial<ShipmentPieceRow>
  ) => {
    setLocalGroups((prev) => {
      const next = [...prev]
      const pieces = [...next[groupIndex].pieces]
      pieces[pieceIndex] = { ...pieces[pieceIndex], ...patch }
      next[groupIndex] = { ...next[groupIndex], pieces }
      return next
    })
    clearServerErrorsForRow(groupIndex)
    clearServerErrorNote(groupIndex)
  }

  const handleAddPiece = (groupIndex: number) => {
    setLocalGroups((prev) => {
      const next = [...prev]
      next[groupIndex] = {
        ...next[groupIndex],
        pieces: [
          ...next[groupIndex].pieces,
          {
            quantity: 1,
            weight: 0,
            height: 0,
            width: 0,
            length: 0,
            declaredValue: 0,
            productName: '',
            productCode: '',
            categoryId: '49',
          },
        ],
      }
      return next
    })
    clearServerErrorsForRow(groupIndex)
    clearServerErrorNote(groupIndex)
  }

  const handleRemovePiece = (groupIndex: number, pieceIndex: number) => {
    setLocalGroups((prev) => {
      const next = [...prev]
      const pieces = next[groupIndex].pieces.filter((_, i) => i !== pieceIndex)
      next[groupIndex] = { ...next[groupIndex], pieces }
      return next
    })
    clearServerErrorsForRow(groupIndex)
    clearServerErrorNote(groupIndex)
  }

  const sendableCount = localGroups.length

  /**
   * Aviso GENERAL de tienda origen ignorada (una sola línea para toda la carga, no
   * una marca por guía).
   *
   * Solo aplica con UNA oficina origen en el contrato: ahí no hay nada que elegir, la
   * columna "Oficina origen" del Excel se ignora y las guías salen igual — pero el
   * operador tiene que saberlo y bajo qué tienda salen. Con VARIAS oficinas no se
   * muestra: `validateGroup` marca cada fila en rojo y el envío queda bloqueado hasta
   * que se elija una válida.
   */
  const singleOriginBu =
    (user.originBusinessUnits?.length ?? 0) === 1
      ? user.originBusinessUnits![0]
      : undefined
  const ignoredOriginCodes = useMemo(
    () =>
      singleOriginBu
        ? unauthorizedOriginStoreCodes(localGroups, [singleOriginBu])
        : [],
    [localGroups, singleOriginBu]
  )

  return (
    <div className="w-full">
      <div className="w-full bg-gray-100 border border-gray-200 rounded-lg p-4 mb-4 grid grid-rows-2 grid-flow-col auto-cols-fr gap-x-8 gap-y-2">
        <span className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {summary.totalGroups}
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-900">
          GUÍAS CARGADAS
        </span>

        <span className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {summary.totalWeight.toFixed(2)} kg
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-900">
          PESO BALANZA TOTAL
        </span>

        <span className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {summary.totalPieces}
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-900">
          PIEZAS EN TOTAL
        </span>

        <span className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          ${summary.totalDeclaredValue.toFixed(2)}
        </span>
        <span className="text-xs font-medium uppercase tracking-wide text-gray-900">
          VALOR DECLARADO
        </span>

        {summary.errorCount > 0 && (
          <>
            <span className="text-2xl font-bold text-red-600 leading-none tabular-nums">
              {summary.errorCount}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-red-700">
              Error
            </span>
          </>
        )}
      </div>

      {ignoredOriginCodes.length > 0 && singleOriginBu && (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="status"
          aria-live="polite"
        >
          Ingresaste una tienda origen ({ignoredOriginCodes.join(', ')}) que no
          está asignada en tu contrato. Todas las guías estarán bajo{' '}
          <strong>{originOfficeDisplay(singleOriginBu as any)}</strong>.
        </div>
      )}

      {coordValidating && (
        <div
          className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 text-sm text-gray-600 mb-4 rounded-lg"
          role="status"
          aria-live="polite"
        >
          Validando coordenadas {coordProgress.current}/{coordProgress.total}...
          <div className="mt-1 h-1 bg-gray-100 rounded overflow-hidden">
            <div
              className="bg-blue-600 h-1 rounded transition-all"
              style={{
                width: `${coordProgress.total > 0 ? Math.round((coordProgress.current / coordProgress.total) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      <ShipmentBulkUploadAccordionList
        groups={localGroups}
        errors={errors}
        identificationTypes={identificationTypes}
        typeShipment={[
          // Carga masiva-only: OFFICE se muestra como "Tienda" (consistente con la plantilla).
          { value: DeliveryEnum.OFFICE, name: 'Tienda' },
          { value: DeliveryEnum.DELIVERY, name: 'Domicilio' },
        ]}
        businessUnits={businessUnits}
        destinationOptions={destinationOptions}
        cities={citiesType}
        citiesByBu={citiesByBu}
        pieceCategories={pieceCategories}
        isCOD={isCOD}
        serviceOptions={serviceOptions}
        onChangeGroup={handleChangeGroup}
        onApplyBulk={handleApplyBulk}
        onChangePiece={handleChangePiece}
        onAddPiece={handleAddPiece}
        onRemovePiece={handleRemovePiece}
        validatePiece={validatePiece}
        onRequestMerge={handleRequestMerge}
        phonePrefixList={phonePrefixList}
        countryOptions={countryOptions}
        countryCatalogFailed={countryCatalogFailed}
        favoriteDestinations={favoriteDestinations}
        favsByRif={favsByRif}
      />

      {mergeModal !== null &&
        (() => {
          const src = localGroups[mergeModal.sourceIndex]
          const target = src
            ? localGroups.find(
                (g, i) =>
                  i !== mergeModal.sourceIndex &&
                  g.shipmentHeaderReference === src.shipmentHeaderReference
              )
            : undefined
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
              role="dialog"
              aria-modal="true"
            >
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h2 className="font-semibold text-gray-800 text-base">
                    Guía duplicada en carga masiva
                  </h2>
                </div>
                <div className="p-4 space-y-3">
                  <p className="text-sm text-gray-600">
                    La guía <strong>{src?.shipmentHeaderReference}</strong> ya existe en
                    esta carga masiva. ¿Deseas fusionar sus piezas con el grupo
                    existente?
                  </p>
                  {target && target.pieces.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2 text-gray-500 font-medium">
                              Categoría
                            </th>
                            <th className="text-right px-3 py-2 text-gray-500 font-medium">
                              Cant.
                            </th>
                            <th className="text-right px-3 py-2 text-gray-500 font-medium">
                              Peso (KG)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {target.pieces.map((p, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2 text-gray-700">
                                {p.productName || p.categoryId}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {p.quantity}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">
                                {p.weight}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setMergeModal(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-md border border-gray-200 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <PrimaryButton
                    onClick={handleConfirmMerge}
                    className="text-sm"
                  >
                    Sí, combinar
                  </PrimaryButton>
                </div>
              </div>
            </div>
          )
        })()}

      {/* FINDING-008 (design-review round 2): sticky bottom submit bar.
          Was: appeared inline at the bottom of the page, requiring users to scroll
          all the way down on long imports (30+ guides) every time they wanted to
          submit. Now sticky to the viewport bottom with white bg + top shadow so
          the submit action is always one click away. */}
      <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 mt-6 px-4 sm:px-6 py-3 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-center justify-between gap-3">
        {serviceOptions.length === 0 && !hasAnyError && (
          <p className="text-sm text-gray-500">
            Cargando servicios disponibles…
          </p>
        )}
        {hasAnyError && serviceOptions.length > 0 && (
          <p className="text-sm text-red-600">
            Hay errores pendientes. Corrígelos antes de enviar.
          </p>
        )}
        {localGroups.some((g) => g.isDuplicateInDB) && (
          <p className="text-sm text-red-600">
            {localGroups.filter((g) => g.isDuplicateInDB).length} guía(s) ya
            existen en el sistema — edita el número de guía antes de enviar.
          </p>
        )}
        <PrimaryButton
          onClick={handleSubmit}
          disabled={
            hasAnyError ||
            sendableCount === 0 ||
            loading ||
            serviceOptions.length === 0
          }
          className="w-full sm:w-auto sm:ml-auto"
        >
          Enviar {sendableCount > 0 ? `${sendableCount} guía(s)` : 'guías'}
        </PrimaryButton>
      </div>

      <ModalLoading
        open={loading}
        title={loadingTitle}
        status={loadingStatus}
        setOpen={setLoading}
        onPrint={() => {}}
      />
    </div>
  )
}

export default ShipmentBulkUploadReview
